"use client";

import { Eligibility } from "@decibeltrade/sdk";
import { useEffect, useRef } from "react";

import { useAnalytics } from "@/contexts/analytics-context";

import type { FftStep } from "./derive-fft-step";

/** Once per distinct blocker set; skips pre-lock, where "no credits" is the default state. */
export function useTrackFftTrialBlocked({
  eligibility,
  step,
  enabled,
}: {
  eligibility: Eligibility;
  step: FftStep;
  enabled: boolean;
}) {
  const analytics = useAnalytics();
  const lastTracked = useRef<string | null>(null);

  // trial_already_active is routine during every open trial, not a funnel blocker.
  const blockerCodes = eligibility.blockerCodes.filter((code) => code !== "trial_already_active");
  const signature =
    eligibility.canOpenTrial || blockerCodes.length === 0
      ? null
      : [...blockerCodes].sort().join("|");
  const { creditsGranted, creditsUsed, softWarnings } = eligibility;

  useEffect(() => {
    if (!enabled || step === "lock") return;
    if (!signature || lastTracked.current === signature) return;
    // Wait for the async rudder instance — don't burn the dedupe on a no-op track.
    if (!analytics.rudder) return;
    lastTracked.current = signature;
    analytics.fft.trialBlocked({
      blockerCodes,
      step,
      creditsRemaining: creditsGranted - creditsUsed,
      dailyBurnNearCap: softWarnings.dailyBurnNearCap,
    });
  }, [
    enabled,
    signature,
    step,
    analytics,
    blockerCodes,
    creditsGranted,
    creditsUsed,
    softWarnings,
  ]);
}
